import { httpClient } from '@/api/httpClient';
import { mapPaginatedResource, type PaginatedResult } from '@/api/pagination.types';
import {
  mapApiResourceToChatAttachmentInit,
  mapApiResourceToChatConversation,
  mapApiResourceToChatMessage,
  mapChatAttachmentInitPayload,
} from './chatMapping';
import {
  type ChatAttachmentInit,
  type ChatAttachmentInitApiResource,
  type ChatAttachmentInitPayload,
  type ChatConversation,
  type ChatConversationApiResource,
  type ChatMessage,
  type ChatMessageApiResource,
  type ChatMessageCursor,
  type ChatScope,
  type ChatUnreadCountApiResource,
} from './chatApi.types';
import type { PaginatedApiResource } from '@/api/pagination.types';

function resolveBasePath(scope: ChatScope): string {
  return scope === 'practitioner' ? '/chat' : '/me/chat';
}

export async function fetchPractitionerConversations(
  page: number,
  search: string
): Promise<PaginatedResult<ChatConversation>> {
  const trimmedSearch = search.trim();
  const response = await httpClient.get<PaginatedApiResource<ChatConversationApiResource>>(
    '/chat/conversations',
    {
      params: {
        page,
        search: trimmedSearch.length > 0 ? trimmedSearch : undefined,
      },
    }
  );
  return mapPaginatedResource(response.data, mapApiResourceToChatConversation);
}

export async function fetchClientConversations(): Promise<ChatConversation[]> {
  const response = await httpClient.get<ChatConversationApiResource[]>('/me/chat/conversations');
  return response.data.map(mapApiResourceToChatConversation);
}

export async function fetchPractitionerConversationById(
  conversationId: string
): Promise<ChatConversation> {
  const response = await httpClient.get<ChatConversationApiResource>(
    '/chat/conversations/' + conversationId
  );
  return mapApiResourceToChatConversation(response.data);
}

export async function createPractitionerConversation(clientId: string): Promise<ChatConversation> {
  const response = await httpClient.post<ChatConversationApiResource>('/chat/conversations', {
    client_id: clientId,
  });
  return mapApiResourceToChatConversation(response.data);
}

export async function fetchConversationMessages(
  scope: ChatScope,
  conversationId: string,
  cursor: ChatMessageCursor
): Promise<ChatMessage[]> {
  const response = await httpClient.get<ChatMessageApiResource[]>(
    resolveBasePath(scope) + '/conversations/' + conversationId + '/messages',
    {
      params: {
        before: cursor.beforeId,
        after: cursor.afterId,
      },
    }
  );
  return response.data.map(mapApiResourceToChatMessage);
}

export async function sendChatMessage(
  scope: ChatScope,
  conversationId: string,
  body: string | null,
  attachmentIds: string[]
): Promise<ChatMessage> {
  const response = await httpClient.post<ChatMessageApiResource>(
    resolveBasePath(scope) + '/conversations/' + conversationId + '/messages',
    {
      body,
      attachment_ids: attachmentIds,
    }
  );
  return mapApiResourceToChatMessage(response.data);
}

export async function markConversationRead(
  scope: ChatScope,
  conversationId: string
): Promise<void> {
  await httpClient.post(resolveBasePath(scope) + '/conversations/' + conversationId + '/read');
}

export async function initChatAttachmentUpload(
  scope: ChatScope,
  conversationId: string,
  payload: ChatAttachmentInitPayload
): Promise<ChatAttachmentInit> {
  const response = await httpClient.post<ChatAttachmentInitApiResource>(
    resolveBasePath(scope) + '/conversations/' + conversationId + '/attachments',
    mapChatAttachmentInitPayload(payload)
  );
  return mapApiResourceToChatAttachmentInit(response.data);
}

export async function fetchChatAttachmentUrl(
  scope: ChatScope,
  attachmentId: string,
  disposition: 'inline' | 'attachment' = 'inline'
): Promise<string> {
  const response = await httpClient.get<{ download_url: string }>(
    resolveBasePath(scope) + '/attachments/' + attachmentId + '/download-url',
    { params: { disposition } }
  );
  return response.data.download_url;
}

export async function fetchChatUnreadCount(scope: ChatScope): Promise<number> {
  const response = await httpClient.get<ChatUnreadCountApiResource>(
    resolveBasePath(scope) + '/unread-count'
  );
  return response.data.unread;
}
